import React, { useEffect, useMemo, useState } from "react";
import FeatherIcon from "feather-icons-react";
import {
  AssignStakeholdersDto,
  StakeholderDto,
  DoubleMaterialityStakeholderType,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { isEmpty } from "lodash";

import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
  Alert,
} from "@components/base";
import Drawer from "@components/common/Drawer";
import IconAssignUsers from "@icons/AssignUsers";
import { useUserStore } from "@store/user";
import { useGetStakeholders } from "@api";
import {
  useAssignStakeholders,
  type AssignStakeholdersResultDto,
} from "@api/hooks/doubleMateriality/AssignStakeholders/useAssignStakeholders";
import { useUnassignStakeholders } from "@api/hooks/doubleMateriality/AssignStakeholders/useUnassignStakeholders";
import { useGetQuestionAnswers } from "@api/hooks/doubleMateriality/GetQuestionAnswers/useGetQuestionAnswers";
import type { QuestionItemType } from "@api";

interface AssignStakeholdersPanelProps {
  open: boolean;
  onClose: () => void;
  labelTitle: string;
  questionIds: string[];
  questions: QuestionItemType[];
  onSaved?: () => void;
}

export const AssignStakeholdersDrawer: React.FC<AssignStakeholdersPanelProps> = ({
  open,
  onClose,
  labelTitle,
  questionIds,
  questions,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const year = parseInt(
    searchParams.get("year") || String(new Date().getFullYear()),
    10,
  );

  const { stakeholders } = useGetStakeholders(user, {
    year,
    pageNumber: 1,
    pageSize: 1000,
  });
  const allStakeholders: StakeholderDto[] = stakeholders?.data ?? [];

  const {
    assignStakeholdersAsync,
    isPendingAssignStakeholders: isAssigning,
  } = useAssignStakeholders(user);
  const {
    unassignStakeholdersAsync,
    isPendingUnassignStakeholders: isUnassigning,
  } = useUnassignStakeholders(user);
  const { answers } = useGetQuestionAnswers(user);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [initialSelectedIds, setInitialSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !questionIds?.length) {
      setSelectedIds([]);
      setInitialSelectedIds([]);

      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      const rows = (answers ?? []) as Array<{
        questionId?: string;
        stakeholderId?: string;
      }>;
      const qSet = new Set(questionIds);
      const ids = new Set<string>();

      rows.forEach((r) => {
        if (r.questionId && r.stakeholderId && qSet.has(r.questionId))
          ids.add(r.stakeholderId);
      });

      if (cancelled) return;
      const idsArray = Array.from(ids);

      setSelectedIds(idsArray);
      setInitialSelectedIds(idsArray);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [answers, open, questionIds.join(",")]);

  const toggleSelected = (id: string): void => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const removeSelected = (id: string): void => {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const isSaving = isAssigning || isUnassigning;

  const selectedChips = useMemo(
    () =>
      selectedIds
        .map((id) => allStakeholders.find((s) => s.id === id))
        .filter(Boolean) as StakeholderDto[],
    [selectedIds, allStakeholders],
  );

  const totalQuestionsCount = questions.length;
  const isPartialSelection = questionIds.length < totalQuestionsCount;

  // Filter stakeholders based on question types - stakeholder type must match question type
  const filteredStakeholders = useMemo(() => {
    const selectedQuestions = questions.filter((q) => questionIds.includes(q.id));
    const hasExternalQuestions = selectedQuestions.some(
      (q) => q.stakeholderType === DoubleMaterialityStakeholderType.External,
    );
    const hasInternalQuestions = selectedQuestions.some(
      (q) => q.stakeholderType === DoubleMaterialityStakeholderType.Internal,
    );

    // If mixed question types, no stakeholders can be assigned to all
    if (hasExternalQuestions && hasInternalQuestions) {
      return [];
    }

    if (hasExternalQuestions) {
      return allStakeholders.filter(
        (s) => s.type === DoubleMaterialityStakeholderType.External,
      );
    }

    if (hasInternalQuestions) {
      return allStakeholders.filter(
        (s) => s.type === DoubleMaterialityStakeholderType.Internal,
      );
    }

    return allStakeholders;
  }, [allStakeholders, questions, questionIds]);

  const showAssignmentSummaryToast = (
    assignmentResults: AssignStakeholdersResultDto[],
  ): void => {
    if (assignmentResults.length === 0) {
      return;
    }

    const summary = assignmentResults.reduce(
      (acc, result) => ({
        assigned: acc.assigned + result.assigned,
        skipped: acc.skipped + result.skipped,
      }),
      { assigned: 0, skipped: 0 },
    );

    if (summary.skipped > 0) {
      enqueueSnackbar(
        t(
          "doubleMateriality.assignStakeholders.partialAssignmentWarning",
          "Assigned {{assigned}} stakeholder(s), skipped {{skipped}}.",
          { assigned: summary.assigned, skipped: summary.skipped },
        ),
        { variant: "warning" },
      );

      return;
    }

    enqueueSnackbar(
      t(
        "doubleMateriality.assignStakeholders.allAssignedSuccess",
        "All selected stakeholders were assigned successfully.",
      ),
      { variant: "success" },
    );
  };

  const onAssignAll = async (): Promise<void> => {
    const stakeholderIds = filteredStakeholders
      .map((stakeholder) => stakeholder.id)
      .filter((id): id is string => Boolean(id));

    if (isEmpty(stakeholderIds)) {
      onClose();

      return;
    }

    const payload: AssignStakeholdersDto = { stakeholderIds };
    const assignmentResults = await Promise.all(
      questionIds.map((id) =>
        assignStakeholdersAsync({ questionId: id, payload }),
      ),
    );

    showAssignmentSummaryToast(assignmentResults);
    onSaved?.();
    onClose();
  };

  const onHideAll = async (): Promise<void> => {
    const stakeholderIds = allStakeholders
      .map((stakeholder) => stakeholder.id)
      .filter((id): id is string => Boolean(id));

    if (isEmpty(stakeholderIds)) {
      onClose();

      return;
    }

    const payload: AssignStakeholdersDto = { stakeholderIds };

    await Promise.all(
      questionIds.map((id) =>
        unassignStakeholdersAsync({ questionId: id, payload }),
      ),
    );
    onSaved?.();
    onClose();
  };

  const onSaveSelected = async (): Promise<void> => {
    // Find stakeholders that were removed (unselected)
    const removedStakeholderIds = initialSelectedIds.filter(
      (id) => !selectedIds.includes(id),
    );

    // Unassign removed stakeholders
    if (removedStakeholderIds.length > 0) {
      const unassignPayload: AssignStakeholdersDto = {
        stakeholderIds: removedStakeholderIds,
      };

      await Promise.all(
        questionIds.map((id) =>
          unassignStakeholdersAsync({ questionId: id, payload: unassignPayload }),
        ),
      );
    }

    // Assign currently selected stakeholders
    let assignmentResults: AssignStakeholdersResultDto[] = [];

    if (selectedIds.length > 0) {
      const assignPayload: AssignStakeholdersDto = { stakeholderIds: selectedIds };

      assignmentResults = await Promise.all(
        questionIds.map((id) =>
          assignStakeholdersAsync({ questionId: id, payload: assignPayload }),
        ),
      );
    }

    showAssignmentSummaryToast(assignmentResults);
    onSaved?.();
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title=""
      content={
        <Box
          width="100%"
          maxHeight="93vh"
          p="0 0 96px 0"
          overflow="auto"
          sx={{ boxSizing: "border-box" }}
        >
          <Grid container rowGap={2} sx={{ px: 5 }}>
            <Grid size={12}>
              <Stack gap={1.5}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <IconAssignUsers sx={{ fontSize: 24 }} />
                  <IconButton onClick={onClose} sx={{ padding: "6px" }}>
                    <FeatherIcon icon="x" />
                  </IconButton>
                </Stack>
                <Stack gap={1}>
                  <Typography fontSize={12} fontWeight={500} color="text.secondary">
                    {t("doubleMateriality.assignDrawer.title", "QUESTION GROUP")}
                  </Typography>
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    color="text.primary"
                    sx={{ lineHeight: "24px" }}
                  >
                    {labelTitle}
                  </Typography>
                  {isPartialSelection && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {t(
                          "doubleMateriality.assignDrawer.selectedQuestions",
                          "Assigning to {{count}} selected questions.",
                          { count: questionIds.length },
                        )}
                      </Typography>
                    </Alert>
                  )}
                </Stack>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Alert color="warning" sx={{ alignItems: "center" }}>
                {t(
                  "assignTo.overwriteWarning",
                  "Assigning stakeholders to the question groups will replace all existing question assignments within them.",
                )}
              </Alert>
            </Grid>
            <Grid size={12}>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={t("assignTo.allStakeholders", "All stakeholders")}
                  color="success"
                  onClick={onAssignAll}
                />
                <Chip
                  label={t("assignTo.hideAll", "Hide from all stakeholder")}
                  color="error"
                  onClick={onHideAll}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("assignTo.selectStakeholders", "Select one or more stakeholders")}
              </Typography>
              <Stack gap={1.5}>
                <Box
                  sx={(theme) => ({
                    border: "1px solid",
                    borderColor: theme.palette.custom.grey[300],
                    borderRadius: "8px",
                    minHeight: 48,
                    px: 1,
                    py: 0.5,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.5,
                  })}
                >
                  {selectedChips.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      {t("assignTo.assignToPlaceholder", "Assign to")}
                    </Typography>
                  ) : null}
                  {selectedChips.map((s) => (
                    <Chip
                      key={s.id}
                      label={s.fullName}
                      onDelete={() => removeSelected(s.id)}
                      size="small"
                      sx={{ m: 0.5, "& .MuiChip-label": { px: 1.25 } }}
                    />
                  ))}
                  <IconAssignUsers sx={{ fontSize: 18, ml: "auto" }} />
                </Box>

                {filteredStakeholders.map((s) => {
                  const isSelected = selectedIds.includes(s.id);

                  return (
                    <Box
                      key={s.id}
                      onClick={() => toggleSelected(s.id)}
                      sx={(theme) => ({
                        border: "1px solid",
                        borderColor: isSelected
                          ? theme.palette.primary.main
                          : theme.palette.custom.grey[200],
                        backgroundColor: isSelected
                          ? theme.palette.custom.blue[50]
                          : theme.palette.common.white,
                        borderRadius: "12px",
                        height: 56,
                        px: 2,
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      })}
                    >
                      <Box
                        sx={(theme) => ({
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.custom.grey[200],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                          fontWeight: 600,
                          fontSize: 12,
                          color: theme.palette.custom.navy[800],
                          textTransform: "uppercase",
                        })}
                      >
                        {s.fullName
                          .split(" ")
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((n) => n[0])
                          .join("")}
                      </Box>
                      <Typography variant="body1">{s.fullName}</Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Grid>
          </Grid>
          <Box
            width="100%"
            p="16px 40px 24px 40px"
            position="absolute"
            bottom={0}
            right={0}
            sx={(theme) => ({
              background: `linear-gradient(to top, ${theme.palette.common.white}, rgba(255,255,255,0.85) 60%, rgba(255,255,255,0))`,
            })}
          >
            <Stack direction="row" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={onClose} disabled={isSaving}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={onSaveSelected}
                disabled={
                  isSaving ||
                  (selectedIds.length === initialSelectedIds.length &&
                    initialSelectedIds.every((id) => selectedIds.includes(id)))
                }
              >
                {t("common.save")}
              </Button>
            </Stack>
          </Box>
        </Box>
      }
    />
  );
};

export default AssignStakeholdersDrawer;
